import { InfoDivider, LAComplete } from "@lyfegen-platform/react-common";
import { LiteBox } from "../../../../../components/lite/LiteBox";
import { useTranslation } from "react-i18next";
import LyfeFormField from "../../../../../components/LyfeFormField";
import { Formik } from "formik";
import * as Yup from "yup";
import { Prompt, useHistory } from "react-router";
import { BottomDrawerUser } from "../../../personal-tab/PersonalTab";
import BottomErrorBanner from "apps/platform-client/client/src/components/errorMessages/BottomErrorBanner";
import { getOperationName } from "@apollo/client/utilities";
import { CREATE_DOMAIN, UPDATE_DOMAIN } from "../../queries";
import { Mutation, Query } from "../../../../../models/generated/graphql";
import { ApolloError } from "@apollo/client/errors";
import { getNetworkErrorStatusCode } from "../../../../../components/SentryLog";
import { Location } from "history";

export interface IDomainInput {
	parentDomainId: string;
	name: string;
}

interface IDomainFormProps {
	initialValues: IDomainInput;
	unavailableDomainNames: Array<string>;
	domainOptions: { id: string; name: string }[];
	handleSubmit: (values: IDomainInput) => void;
	title: string;
	label: string;
	noParent?: boolean;
	endAdornment?: () => JSX.Element;
	isError?: boolean;
	error?: ApolloError | null;
	dismissBanner?: () => void;
	isDeleting?: boolean;
}

export const DomainForm = (props: IDomainFormProps) => {
	const { initialValues, unavailableDomainNames, domainOptions, noParent, handleSubmit } = props;
	const { t } = useTranslation();
	const { push } = useHistory();

	const schema = Yup.object().shape({
		parentDomainId: !!noParent ? Yup.string() : Yup.string().required(t("is-required-field")),
		name: Yup.string()
			.required(`${t(props.label)} ${t("is-required-field")}`)
			.max(60)
			.transform((value) => value?.toLowerCase().trim())
			.notOneOf(unavailableDomainNames, t("domain-name-duplicate")),
	});

	const handleCancel = () => {
		push("/organization-settings/domains");
	};

	const handlePromptMessage = (location: Location) => {
		return location.hash ? true : t("changes-made-not-saved");
	};

	return (
		<Formik
			initialValues={initialValues}
			onSubmit={handleSubmit}
			validationSchema={schema}
			validateOnChange
			validateOnMount
			enableReinitialize
		>
			{({ initialValues, values, submitForm, isValid, isSubmitting, dirty }) => {
				const bottomDrawerProps = {
					submitForm,
					resetForm: handleCancel,
					isValid,
					initialValues,
					values,
					isSubmitting,
				};
				const blockNavigation = !props.isDeleting && !isSubmitting && initialValues.name !== values.name;

				return (
					<LiteBox>
						<Prompt when={blockNavigation} message={handlePromptMessage} />
						<InfoDivider paddingX={2} paddingBottom={4} paddingTop={0} endAdornment={props.endAdornment} title={props.title} />
						{/* LEVEL 1, 2 */}
						<LiteBox display="flex">
							{!noParent && (
								<LiteBox paddingLeft={2} width="20%">
									<LAComplete
										title={t("subdomain-owned-by")}
										name={"parentDomainId"}
										property="id"
										options={domainOptions}
										getOptionLabel={(v: any) => v?.name}
										selectPropertyValue
										disabled
									/>
								</LiteBox>
							)}
							<LiteBox paddingLeft={noParent ? 2 : 4} width="20%">
								<LyfeFormField name="name" height={16} fullWidth label={t(props.label)} required />
							</LiteBox>
						</LiteBox>
						<BottomDrawerUser {...bottomDrawerProps} />
						{props.isError && (
							<BottomErrorBanner
								statusCode={getNetworkErrorStatusCode(props.error?.networkError)}
								isOpen={props.isError}
								onClose={() => props.dismissBanner?.()}
								errorTitle={
									props.endAdornment ? t("simulator-update-domain-error-p1") : t("simulator-create-domain-error-p1")
								}
								secondaryText={
									props.endAdornment ? t("simulator-update-domain-error-p2") : t("simulator-create-domain-error-p2")
								}
								handleTryAgain={() => handleSubmit(values)}
								error={props.error}
								operation={
									props.endAdornment
										? (getOperationName(UPDATE_DOMAIN) as keyof Mutation | keyof Query)
										: (getOperationName(CREATE_DOMAIN) as keyof Mutation | keyof Query)
								}
							/>
						)}
					</LiteBox>
				);
			}}
		</Formik>
	);
};
